import {
  AgentThreadMessage,
  AgentThreadRole,
  isAgentThreadTextContent,
  ClientViewingContext,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import { VOLATILE_VISUALIZER_STATE_FIELDS } from "@/shared/services";

import { isEmptyContext } from "../contextUtils";

const CTX_REGEX = /^<ctx>The above was sent with context: (.+)<\/ctx>$/;

/**
 * Attempts to parse a ClientViewingContext from a ROBOTO-role message
 * that contains a `<ctx>...</ctx>` text block.
 *
 * The parsed JSON is whatever the client recorded at send time, which for an
 * existing thread predates fields added since — a thread from before
 * collection/device context existed carries no such keys. Every id list is
 * therefore defaulted to `[]` here, at the single point untrusted stored JSON
 * enters, so downstream readers can take `.length` unconditionally.
 */
export function parseContextFromMessage(
  message: AgentThreadMessage,
): ClientViewingContext | null {
  if (message.role !== AgentThreadRole.ROBOTO) {
    return null;
  }

  for (const content of message.content) {
    if (!isAgentThreadTextContent(content)) {
      continue;
    }

    const match = content.text.match(CTX_REGEX);
    if (match) {
      try {
        const parsed = JSON.parse(match[1]) as Partial<ClientViewingContext>;
        return {
          ...parsed,
          dataset_ids: parsed.dataset_ids ?? [],
          file_ids: parsed.file_ids ?? [],
          collection_ids: parsed.collection_ids ?? [],
          device_ids: parsed.device_ids ?? [],
        };
      } catch {
        return null;
      }
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Incremental scan infrastructure
// ---------------------------------------------------------------------------

export interface ScanState {
  previousContext: ClientViewingContext | null;
}

export interface ScanResult {
  shifts: Map<number, ClientViewingContext>;
  endState: ScanState;
}

/**
 * Scans messages starting at `fromIndex` and returns context shifts found,
 * plus the end state so callers can resume later without re-processing.
 *
 * Empty contexts are only emitted when transitioning from non-empty → empty
 * (user navigated away). Initial empty contexts (when previousContext is null
 * or also empty) are suppressed.
 */
export function scanForShifts(
  messages: AgentThreadMessage[],
  fromIndex: number,
  initialState: ScanState,
): ScanResult {
  const shifts = new Map<number, ClientViewingContext>();
  let previousContext = initialState.previousContext;

  for (let i = fromIndex; i < messages.length; i++) {
    const ctx = parseContextFromMessage(messages[i]);
    if (ctx === null) {
      continue;
    }

    // Skip empty contexts when there is no previous non-empty context
    // (i.e. the conversation starts with empty context — nothing to show).
    // But DO show empty contexts when transitioning from non-empty → empty,
    // since the user navigated away from something.
    if (
      isEmptyContext(ctx) &&
      (previousContext === null || isEmptyContext(previousContext))
    ) {
      previousContext = ctx;
      continue;
    }

    if (!contextsEqual(previousContext, ctx)) {
      // Walk backwards from the ctx message to find the preceding USER message
      const userIndex = findPrecedingUserMessage(messages, i);
      if (userIndex !== null) {
        shifts.set(userIndex, ctx);
      }
    }
    previousContext = ctx;
  }

  return { shifts, endState: { previousContext } };
}

// ---------------------------------------------------------------------------
// Public API — convenience wrapper for full-scan use cases and tests
// ---------------------------------------------------------------------------

/**
 * Scans a list of messages and returns context shifts — positions where
 * the embedded context changed compared to the previous context.
 *
 * Returns a Map keyed by the index of the USER message that precedes each
 * context message. This allows rendering the indicator above the user's
 * message rather than below it.
 *
 * Empty contexts are only shown when the user transitions from a non-empty
 * context to an empty one (i.e. navigated away). An initial empty context
 * at the start of a conversation is suppressed.
 */
export function detectContextShifts(
  messages: AgentThreadMessage[],
): Map<number, ClientViewingContext> {
  return scanForShifts(messages, 0, { previousContext: null }).shifts;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Walks backwards from `startIndex` to find the first USER message.
 */
function findPrecedingUserMessage(
  messages: AgentThreadMessage[],
  startIndex: number,
): number | null {
  for (let j = startIndex - 1; j >= 0; j--) {
    if (messages[j].role === AgentThreadRole.USER) {
      return j;
    }
  }
  return null;
}

/**
 * Order-sensitive equality for a context's entity id lists. Order is
 * significant because the lists are built deterministically from the page's
 * viewing context, so a reordering would mean the underlying view changed.
 */
function idListsEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

function contextsEqual(
  a: ClientViewingContext | null,
  b: ClientViewingContext,
): boolean {
  if (a === null) {
    return false;
  }

  // Compare the entity id lists
  if (
    !idListsEqual(a.dataset_ids, b.dataset_ids) ||
    !idListsEqual(a.file_ids, b.file_ids) ||
    !idListsEqual(a.collection_ids, b.collection_ids) ||
    !idListsEqual(a.device_ids, b.device_ids)
  ) {
    return false;
  }

  // Compare visualizer_state presence
  const aViz = a.visualizer_state;
  const bViz = b.visualizer_state;
  const aHasViz = aViz !== undefined && aViz !== null;
  const bHasViz = bViz !== undefined && bViz !== null;
  if (aHasViz !== bHasViz) {
    return false;
  }
  if (aHasViz && bHasViz) {
    // Live fields like the playhead advance with every message send, so a
    // raw deep-equal would treat every turn as a context shift and render a
    // "context changed" indicator above every user message. Strip volatile
    // fields first so only structural changes (panels added, file pinned,
    // etc.) count.
    if (
      JSON.stringify(stripVolatileVizFields(aViz)) !==
      JSON.stringify(stripVolatileVizFields(bViz))
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Returns a shallow copy of `visualizer_state` with the volatile keys
 * declared by the live-state bridge removed, so contextsEqual diffs only
 * structural state (panels, pinned files, etc.). Callers compare presence
 * before stripping, so `state` is always a real record here.
 */
function stripVolatileVizFields(
  state: Record<string, unknown>,
): Record<string, unknown> {
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(state)) {
    if (!VOLATILE_VISUALIZER_STATE_FIELDS.has(key)) {
      filtered[key] = value;
    }
  }
  return filtered;
}
